import type { ClientMemory } from './types/client-error';

/** Non-standard, Chromium only, and the only heap reading a page can take about itself. */
type PerformanceWithMemory = Performance & {
    memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number };
};

const BYTES_PER_MB = 1024 * 1024;

/**
 * Samples the JavaScript heap.
 *
 * A tab killed by the operating system for using too much memory leaves nothing behind: no error, no
 * rejection, no unload. The only evidence that it was memory is a heap reading taken shortly before,
 * which is why this is sampled on a heartbeat rather than only when something already went wrong.
 *
 * @returns The reading in megabytes, or `undefined` outside Chromium.
 */
export function collectMemory(): ClientMemory | undefined {
    const memory = typeof performance === 'undefined' ? undefined : (performance as PerformanceWithMemory).memory;
    if (!memory || !memory.jsHeapSizeLimit) {
        return undefined;
    }

    return {
        usedJsHeapMb: round(memory.usedJSHeapSize / BYTES_PER_MB),
        totalJsHeapMb: round(memory.totalJSHeapSize / BYTES_PER_MB),
        jsHeapLimitMb: round(memory.jsHeapSizeLimit / BYTES_PER_MB),
        pressure: round(memory.usedJSHeapSize / memory.jsHeapSizeLimit),
    };
}

function round(value: number): number {
    return Math.round(value * 100) / 100;
}
